/**
 * Client-Side Image Compressor
 * Automatically compresses images before upload if they exceed size limit
 * Sprint 4I
 */

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_WIDTH = 2048; // Max width before compression
const MAX_HEIGHT = 2048; // Max height before compression
const QUALITY = 0.85; // JPEG quality (0-1)

export interface CompressionResult {
  file: File;
  originalSize: number;
  compressedSize: number;
  wasCompressed: boolean;
}

/**
 * Compress an image file if it exceeds the size limit
 * 
 * @param file - Original image file
 * @returns Compressed file (or original if under limit)
 */
export async function compressImage(file: File): Promise<CompressionResult> {
  const originalSize = file.size;
  
  // If file is already under the limit and reasonable dimensions, return as-is
  if (originalSize <= MAX_FILE_SIZE) {
    // Still check dimensions
    const needsResize = await checkIfNeedsResize(file);
    if (!needsResize) {
      return {
        file,
        originalSize,
        compressedSize: originalSize,
        wasCompressed: false,
      };
    }
  }
  
  console.log(`[ImageCompressor] Compressing image: ${file.name} (${(originalSize / 1024 / 1024).toFixed(2)} MB)`);
  
  try {
    // Load image
    const img = await loadImage(file);
    
    // Calculate new dimensions
    const { width, height } = calculateDimensions(img.width, img.height);
    
    // Create canvas and resize
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Failed to get canvas context');
    }
    
    // Use high-quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    
    // Draw resized image
    ctx.drawImage(img, 0, 0, width, height);
    
    // Convert to Blob with compression
    const blob = await canvasToBlob(canvas, file.type, QUALITY);
    
    // Convert Blob to File
    const compressedFile = new File(
      [blob],
      file.name,
      { type: file.type, lastModified: Date.now() }
    );
    
    const compressedSize = compressedFile.size;
    
    console.log(`[ImageCompressor] Compressed: ${(originalSize / 1024 / 1024).toFixed(2)} MB → ${(compressedSize / 1024 / 1024).toFixed(2)} MB (${Math.round((1 - compressedSize / originalSize) * 100)}% reduction)`);
    
    return {
      file: compressedFile,
      originalSize,
      compressedSize,
      wasCompressed: true,
    };
  } catch (error: any) {
    console.error('[ImageCompressor] Compression failed:', error);
    // Return original file if compression fails
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      wasCompressed: false,
    };
  }
}

/**
 * Check if image needs resizing based on dimensions
 */
async function checkIfNeedsResize(file: File): Promise<boolean> {
  const img = await loadImage(file);
  return img.width > MAX_WIDTH || img.height > MAX_HEIGHT;
}

/**
 * Load an image file into an HTMLImageElement
 */
function loadImage(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 */
function calculateDimensions(width: number, height: number): { width: number; height: number } {
  // If already within limits, return original
  if (width <= MAX_WIDTH && height <= MAX_HEIGHT) {
    return { width, height };
  }
  
  const aspectRatio = width / height;
  
  if (width > height) {
    // Landscape
    return {
      width: Math.min(width, MAX_WIDTH),
      height: Math.min(width, MAX_WIDTH) / aspectRatio,
    };
  } else {
    // Portrait or square
    return {
      width: Math.min(height, MAX_HEIGHT) * aspectRatio,
      height: Math.min(height, MAX_HEIGHT),
    };
  }
}

/**
 * Convert canvas to Blob with specified quality
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    // Use JPEG for better compression (unless it's PNG with transparency)
    const outputType = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
    
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create blob'));
        }
      },
      outputType,
      quality
    );
  });
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}








